// ink-component
import React, { HTMLAttributes, ReactNode } from 'react';
import { StyledComponentPropsWithAs } from 'babel-plugin-styled-components';
import { ThemeProvider } from 'styled-components';
import { CustomTextElement } from './styles';
import { FeedbackVariants } from '../../_common/Types/GlobalTypes';
import getGlobalProps from '../../../helpers/constants/global-props';

export type Weights =
  | 'inherit'
  | '300'
  | '400'
  | '500'
  | '600'
  | '700'
  | 'light'
  | 'regular'
  | 'medium'
  | 'semibold'
  | 'bold';

export type Sizes =
  | 'inherit'
  | '90'
  | '72'
  | '60'
  | '48'
  | '36'
  | '30'
  | '24'
  | '20'
  | '18'
  | '16'
  | '14'
  | '13'
  | '12'
  | '11'
  | 'xlarge'
  | 'large'
  | 'body'
  | 'small'
  | 'xsmall';

export interface CustomTextProps extends StyledComponentPropsWithAs, Omit<HTMLAttributes<HTMLElement>, 'aria-level'> {
  /** A **textual** HTML tag to be used when rendering the text node, like 'p', 'h6' or 'small' for example. */
  as?: string;
  /** Horizontal alignment of the text inside `children` */
  align?: 'inherit' | 'left' | 'center' | 'right';
  /** @ignore */
  variant?:
    | Extract<FeedbackVariants, 'feedback-positive' | 'feedback-negative' | 'feedback-neutral'>
    | 'display-1'
    | 'display-1-feedback-positive'
    | 'display-1-feedback-negative'
    | 'display-2'
    | 'display-2-feedback-positive'
    | 'display-2-feedback-negative'
    | 'heading-1'
    | 'heading-2'
    | 'heading-3'
    | 'heading-4'
    | 'body-1'
    | 'body-2'
    | 'body-3'
    | 'body-3-feedback-neutral'
    | 'body-3-feedback-positive'
    | 'body-3-feedback-negative'
    | 'small-1'
    | 'small-2'
    | 'monospace';

  /** Text color. Prefer using `variant` to use semantic feedback color */
  color?: 'inherit' | 'black' | 'gray' | 'white' | 'blue' | 'red' | 'green';
  /** Overrides the default display of the HTML tag passed in the `as` prop */
  display?: 'block' | 'inline';
  /** The overall size of the text, where `body` and `14` are the same, representing the body font size. */
  size?: Sizes;
  /** A way to transform the casing aspect of the string inside `children`.
   * This is a cosmetic only change, give preference to treat strings prior
   * to passing it to the component. */
  textTransform?: 'none' | 'uppercase' | 'lowercase' | 'capitalize';
  /** Removes the white space below the text. Values `none` and `both` are being deprecated */
  trim?: 'none' | 'both' | boolean;
  /** Changes the font weight of the text inside `children` */
  weight?: Weights;
  /** Allows control over text wrapping */
  whiteSpace?: 'inherit' | 'normal' | 'nowrap';
  /** Be mindful of how it affects the display of numbers. Use when user/unknown content risks critical layout breakage. */
  wordBreak?: React.CSSProperties['wordBreak'];
  /** Content */
  children?: ReactNode;
  /** Defines the hierarchical level of the element within the page structure
   * when not using "as". 'h1', for example, should be aria-level="1" */
  'aria-level'?: string | number;
}

/**
 * @purpose Displays pleasant typography in web interfaces
 * @group Typography
 * @categories typography
 * @aka text
 */
export function CustomText(props: CustomTextProps) {
  const {
    align,
    as,
    color,
    display,
    size,
    textTransform,
    trim,
    weight,
    whiteSpace,
    wordBreak,
    variant,
    style,
    className,
    ...otherProps
  } = props;

  const variantAs = {
    'display-1': 'p',
    'display-2': 'p',
    'heading-1': 'h1',
    'heading-2': 'h2',
    'heading-3': 'h3',
    'heading-4': 'h4',
    'body-1': 'p',
    'body-2': 'p',
    'body-3': 'p',
    'small-1': 'p',
    'small-2': 'p',
    monospace: 'span',
  };

  const globalProps = getGlobalProps({
    sunset: props['data-ink-sunset'],
  });

  return (
    <ThemeProvider
      theme={currentTheme => ({
        ...currentTheme,
        customTextAlign: align,
        customTextColor: color,
        customTextDisplay: display,
        customTextSize: size,
        customTextTransform: textTransform,
        customTextTrim: trim,
        customTextWeight: weight,
        customTextWhiteSpace: whiteSpace,
        customTextWordBreak: wordBreak,
        customTextVariant: variant,
      })}
    >
      <CustomTextElement
        as={variant && !as ? variantAs[variant] : as}
        data-testid={props['data-testid']}
        {...otherProps}
        {...globalProps}
      >
        {props.children}
      </CustomTextElement>
    </ThemeProvider>
  );
}

CustomText.defaultProps = {
  align: 'inherit',
  children: undefined,
  color: 'inherit',
  display: undefined,
  size: 'body',
  textTransform: 'none',
  trim: false,
  weight: 'inherit',
  whiteSpace: 'inherit',
  wordBreak: 'inherit',
};

CustomText.displayName = 'CustomText';

export default CustomText;
